/*
  Purpose-built interaction primitives:
  - Tilt: pointer-only depth, no mount work
  - Reveal: content is always paintable; signature CSS owns optional motion
  - Parallax: one transform-only listener, disabled for reduced motion
  - Clocks and language switch: the Basel/Nairobi operating anchors
*/
import { ReactNode, useEffect, useRef, useState } from "react";
import { LANGS, useI18n } from "@/lib/i18n";

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function Parallax({ children, strength = 0.15, className = "" }: { children: ReactNode; strength?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reducedMotion()) return;
    const el = ref.current;
    if (!el) return;
    let ticking = false;

    function update() {
      ticking = false;
      const rect = el!.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const progress = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
      const shift = Math.max(-1, Math.min(1, progress)) * strength * 100;
      el!.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
    }

    function onScroll() {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    }

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [strength]);

  return <div ref={ref} className={className}>{children}</div>;
}

export function Tilt({ children, max = 6, className = "" }: { children: ReactNode; max?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);

  function onMove(event: React.PointerEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el || reducedMotion() || event.pointerType === "touch") return;
    const rect = el.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width - 0.5;
    const y = (event.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `rotateY(${x * max}deg) rotateX(${-y * max}deg) translateZ(6px)`;
  }

  return (
    <div className={`tilt-scene ${className}`} onPointerMove={onMove} onPointerLeave={() => { if (ref.current) ref.current.style.transform = ""; }}>
      <div ref={ref} className="tilt-body h-full">{children}</div>
    </div>
  );
}

/* Critical content is never hidden behind IntersectionObserver state. This
   removes dozens of hydration-time observers/state updates from long pages and
   prevents scroll effects from becoming an LCP dependency. */
export function Reveal({ children, delay = 0, className = "" }: { children: ReactNode; delay?: number; className?: string }) {
  return (
    <div className={`reveal in ${className}`} style={delay ? { "--reveal-delay": `${delay}ms` } as React.CSSProperties : undefined}>
      {children}
    </div>
  );
}

function timeIn(timeZone: string) {
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", timeZone }).format(new Date());
}

type ClockTimes = { basel: string; nairobi: string };
const CLOCK_PLACEHOLDER: ClockTimes = { basel: "--:--", nairobi: "--:--" };

export function Clocks({ className = "" }: { className?: string }) {
  const { t } = useI18n();
  const [times, setTimes] = useState<ClockTimes>(CLOCK_PLACEHOLDER);

  useEffect(() => {
    const update = () => setTimes({ basel: timeIn("Europe/Zurich"), nairobi: timeIn("Africa/Nairobi") });
    update();
    const id = window.setInterval(update, 30_000);
    return () => window.clearInterval(id);
  }, []);

  return (
    <span className={`text-[10px] font-semibold tracking-[0.14em] text-[rgba(243,234,225,0.6)] tabular-nums whitespace-nowrap ${className}`}>
      {t.common.baselTime} {times.basel} · {t.common.nairobiTime} {times.nairobi}
    </span>
  );
}

export function LangSwitch({ className = "" }: { className?: string }) {
  const { lang, setLang } = useI18n();
  return (
    <div className={`flex items-center gap-1.5 ${className}`} role="group" aria-label="Language">
      {LANGS.map((language) => (
        <button
          key={language.code}
          type="button"
          onClick={() => setLang(language.code)}
          className={`lang-btn ${lang === language.code ? "active" : ""}`}
          aria-pressed={lang === language.code}
        >
          {language.label}
        </button>
      ))}
    </div>
  );
}
